import { useEffect, useState } from 'react';
import { PriceTableDto } from '@tiendanube/common';
import { Card } from '@tiendanube/components';
import useCustomerPriceTables from 'domains/Customers/CustomerPriceTables/hooks/useCustomerPriceTables';
import PriceTablesAddState from 'domains/Customers/Customers/pages/CustomerFormPage/components/CustomerPriceTablesCard/components/PriceTablesAddState';
import PriceTablesEditState from 'domains/Customers/Customers/pages/CustomerFormPage/components/CustomerPriceTablesCard/components/PriceTablesEditState';
import PriceTablesErrorState from 'domains/Customers/Customers/pages/CustomerFormPage/components/CustomerPriceTablesCard/components/PriceTablesErrorState';
import PriceTablesLoadingState from 'domains/Customers/Customers/pages/CustomerFormPage/components/CustomerPriceTablesCard/components/PriceTablesLoadingState';
import PriceTablesModal from 'domains/Customers/Customers/pages/CustomerFormPage/components/CustomerPriceTablesCard/components/PriceTablesModal';
import { useTranslationCustomers } from 'domains/Customers/hooks';
import usePriceTables from 'domains/Customers/PriceTables/hooks/usePriceTables';

export type PriceTableInCard = Pick<
  PriceTableDto,
  'id' | 'name' | 'defaultValue'
>;

export interface CustomerPriceTablesCardProps {
  customerId?: string;
  formStatePriceTables: string[];
  onChangePriceTables: (priceTablesIds: string[]) => void;
}

export default function CustomerPriceTablesCard({
  customerId,
  formStatePriceTables,
  onChangePriceTables,
}: Readonly<CustomerPriceTablesCardProps>) {
  const t = useTranslationCustomers();
  const { priceTablesStatus, fetchPriceTables, priceTablesData } =
    usePriceTables();
  const {
    customerPriceTablesStatus,
    customerPriceTablesData,
    fetchCustomerPriceTables,
    resetCustomerPriceTables,
  } = useCustomerPriceTables(customerId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPriceTables, setSelectedPriceTables] = useState<
    PriceTableInCard[]
  >([]);

  useEffect(() => {
    const selectedData: PriceTableInCard[] = [];
    for (const id of formStatePriceTables) {
      const data = priceTablesData.find((priceTable) => priceTable.id === id);
      if (data) {
        selectedData.push({
          id,
          name: data.name,
          defaultValue: data.defaultValue,
        });
      }
    }

    setSelectedPriceTables(selectedData);
  }, [formStatePriceTables, priceTablesData]);

  useEffect(() => {
    fetchPriceTables();
    fetchCustomerPriceTables();

    return () => resetCustomerPriceTables();
  }, [fetchPriceTables, fetchCustomerPriceTables, resetCustomerPriceTables]);

  useEffect(() => {
    if (customerPriceTablesStatus.isSuccess && customerPriceTablesData.length) {
      onChangePriceTables([customerPriceTablesData[0].id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPriceTablesStatus.isSuccess, customerPriceTablesData]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const addPriceTable = (priceTable: PriceTableInCard) => {
    onChangePriceTables([...formStatePriceTables, priceTable.id]);
  };

  const removePriceTable = (id: string) => {
    const filteredPriceTables = formStatePriceTables.filter(
      (priceTable) => priceTable !== id,
    );
    onChangePriceTables(filteredPriceTables);
  };

  const addOnlyOnePriceTable = (priceTable: PriceTableInCard) => {
    onChangePriceTables([priceTable.id]);
  };

  if (priceTablesStatus.isLoading || customerPriceTablesStatus.isLoading) {
    return <PriceTablesLoadingState />;
  }

  if (priceTablesStatus.isError) {
    return <PriceTablesErrorState onRetry={fetchPriceTables} />;
  }

  if (customerPriceTablesStatus.isError) {
    return <PriceTablesErrorState onRetry={fetchCustomerPriceTables} />;
  }

  return (
    <Card title={t('customerForm.priceTablesInformation.title')}>
      {selectedPriceTables.length === 0 ? (
        <PriceTablesAddState openModal={openModal} />
      ) : (
        <PriceTablesEditState
          openModal={openModal}
          priceTables={selectedPriceTables}
          removePriceTable={removePriceTable}
        />
      )}

      <PriceTablesModal
        isOpen={isModalOpen}
        close={closeModal}
        priceTables={priceTablesData}
        selectedPriceTables={selectedPriceTables}
        addPriceTable={addPriceTable}
        removePriceTable={removePriceTable}
        addOnlyOnePriceTable={addOnlyOnePriceTable}
      />
    </Card>
  );
}
